import './App.css';
import './index.css';
import axios from 'axios';
import { useEffect, useState, SetStateAction, useRef, useContext } from 'react';

import Pagination from '@mui/material/Pagination';
import { TextField } from '@mui/material';
import { Slider, CircularProgress } from '@mui/material';

import { User } from './types/types';

import SearchResultsContainer from './components/SearchResultsContainer';

import { AlgoliaProvider, AlgoliaContext } from './context/AlgoliaContext';
import PlacesAutocompleteComponent from './components/PlacesAutocompleteComponent';
import CategoryFilters from './components/CategoryFilters';

declare global {
  interface Window { 
    dev: boolean | null; 
    algolia_app_id: string; 
    algolia_search_only_key: string; 
    categoryName: string | null;
    categoryDescription: string | null; 
  }
}

const AppContent = () => {

  const {
    isMobile,
    domain,
    getCurrentTidParams
  } = useContext(AlgoliaContext);

  function stripHTMLTags(input: string): string {
    return input.replace(/<[^>]*>/g, '');
  }

  function truncateWithReadMore(text: string, maxLength: number, url: string): JSX.Element {
    if (text.length <= maxLength) {
      return <>{text}</>;
    }

    const truncatedText = text.substring(0, maxLength) + '...';

    return (<>{truncatedText}<a href={url}> Read more</a></>);
  }

  const [algoliaDistance, setAlgoliaDistance] = useState<number>(501);

  interface MilesSliderProps {
    className: string;
    algoliaDistance: number;
    setAlgoliaDistance: React.Dispatch<SetStateAction<number>>;
  }

  const MilesSlider: React.FC<MilesSliderProps> = ({ className, algoliaDistance, setAlgoliaDistance }) => {

    const [value, setValue] = useState<number>(algoliaDistance);

    const handleChange = (event: Event, newValue: number | number[]) => {
      setValue(newValue as number);
    };

    const formatLabel = (value: number): string => {
      return `${value > 500 ? "+500" : value} kilometer${value === 1 ? '' : 's'}`;
    };

    return (
      <div className={className}>
        <Slider
          min={1}
          max={501}
          step={1}
          value={value}
          onChange={handleChange}
          onChangeCommitted={(event, value) => { setAlgoliaDistance((value as number)) }}
          valueLabelDisplay="auto"
          valueLabelFormat={formatLabel}
        />
        <p className='ml-10 w-[200px]'>{formatLabel(value)}</p>
      </div>
    );
  };

  const [query, setQuery] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const q = searchParams.get("q");
    return q ?? "";
  });

  const [searchResults, setSearchResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  interface AlgoliaRequestParams {
    query: any;
    page: any;
    filters?: string;
    aroundLatLng?: string;
    aroundRadius?: number;
  }

  const [categoryFilters, setCategoryFilters] = useState<string[] | null>(getCurrentTidParams());

  const [categoryName, setCategoryName] = useState<string | null>(window.categoryName ?? null);
  const [categoryDescription, setCategoryDescription] = useState<string | null>(window.categoryDescription ?? null);

  const [loadingCategoryData, setLoadingCategoryData] = useState<boolean>(false);

  useEffect(() => {

    if (categoryFilters && categoryFilters.length === 1 && categoryFilters[0]) {
      
      const fetchCategoryDescription = async () => {
        setLoadingCategoryData(true);
        const response = await axios.post(domain + '/api/widget/html/get/fetch-category-description', { category_id: categoryFilters[0] });
        setCategoryName(response.data.category_name);
        setCategoryDescription(response.data.category_description);
        setLoadingCategoryData(false);
      };

      fetchCategoryDescription();

    } else {
      setCategoryName(null);
      setCategoryDescription(null);
    }

  }, [categoryFilters]);

  const searchWithAlgolia = (query: any, page: any, distance: number, lat: number, lon: number, coords:Location, categoryFilters:string[] | null) => {

    const ALGOLIA_APP_ID: string = window.algolia_app_id ?? "JQ9FLN3XYG";
    const ALGOLIA_API_KEY: string = window.algolia_search_only_key ?? "7c8407cacf5f7a43d7dc2c4ead545fa9";
    const ALGOLIA_INDEX_NAME = 'users_data';

    const client = axios.create({
      baseURL: `https://${ALGOLIA_APP_ID}.algolia.net/1/indexes/${ALGOLIA_INDEX_NAME}`,
      headers: {
        'X-Algolia-API-Key': ALGOLIA_API_KEY,
        'X-Algolia-Application-Id': ALGOLIA_APP_ID
      }
    });

    // Add categoryFilters to the filters if it exists
    let filters = "";
    if (categoryFilters && categoryFilters.length > 0) {
      filters = categoryFilters.map(filter => `category_ids:${encodeURIComponent(filter)}`).join(' AND ');
    }    

    // Initialize request parameters
    let requestParams: AlgoliaRequestParams = {
      query: query,
      page: page,
      filters: filters,
    };

    // Add aroundLatLng and aroundRadius to request parameters if distance <= 500
    if (coords.lat && coords.lng) {
      requestParams = {
        ...requestParams,
        aroundLatLng: `${coords.lat},${coords.lng}`,
        aroundRadius: distance * 1000
      };
    };

    client.get('', {
      params: requestParams
    }).then(response => {
      setSearchResults(response.data.hits);
      setTotalResults(response.data.nbHits);
      setTotalPages(response.data.nbPages);
    }).catch(error => {
      console.log(error);
    });

  };

  const [location, setLocation] = useState({ lat: 0, lon: 0 });

  interface Location {
    lat: number | null;
    lng: number | null;
  }

  const [coords, setCoords] = useState<Location>({
    lat: null,
    lng: null
  });

  useEffect(() => {
    searchWithAlgolia(query, page, algoliaDistance, location.lat, location.lon, coords, categoryFilters);
  }, [query, page, algoliaDistance, location, coords, categoryFilters]);

  useEffect(() => {
    setPage(0);
  }, [query, location, coords, categoryFilters]);

  type DebouncedFunction<T extends (...args: any[]) => any> =
    (this: ThisParameterType<T>, ...args: Parameters<T>) => void;

  function debounce<T extends (...args: any[]) => any>(fn: T, wait: number): DebouncedFunction<T> {
    let timeout: any;
    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const debouncedSetAlgoliaQuery = debounce(setQuery, 50);

  const [activeLocationName, setActiveLocationName] = useState('');

  const locationSearchInputRef = useRef<any>();

  const scrollToElementById = (id: string): void => {
    const element = document.getElementById(id);
  
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  };
  

  useEffect(() => {

    scrollToElementById('search-results-top');

  }, [page]);

  const KeywordAndLocation = () => {
    return (

      <div className='w-full md:mb-0 mb-8 space-y-6'>
        <div className='w-full flex flex-col justify-center space-y-3'>

          <p className='hidden md:flex'>Keyword Search:</p>
          <TextField className="w-full bg-white" defaultValue={query} id="outlined-basic" label="Search for Providers or Services" variant="outlined" onChange={(e) => debouncedSetAlgoliaQuery(e.target.value)} />
      
        </div>

        <div className='w-full flex flex-col justify-center space-y-3'>

          <p className='hidden md:flex'>Location Search:</p>
          <PlacesAutocompleteComponent 
          
            className="bg-white text-black"
            getCoords={setCoords}
            setActiveLocationName={setActiveLocationName}
            activeLocationName={activeLocationName}
            ref={locationSearchInputRef}

          />

        </div>
      </div>

    )
  };

  return (
    <div className='flex flex-col space-y-5 w-full'>
      {
        (() => {
          if (window.dev) {

            if (loadingCategoryData) {
              return (
                <div className='w-full h-full flex justify-center items-center'>
                  <CircularProgress />
                </div>
              )
            } else {

              return (
                <div className='py-5 pb-10 flex flex-col space-y-3'>
                  {
                    categoryName && 
                    <h1 className="font-bold mb-[10px]">{categoryName}</h1>
                  }
                  <div dangerouslySetInnerHTML={{ __html: categoryDescription ? stripHTMLTags(categoryDescription) : '' }}></div>
                </div>
              )

            }
          }
        })()
      }

      <div className="w-full flex flex-col-reverse md:flex-row justify-center items-center md:items-start md:space-x-[35px]">

        <div id="filters-area" className='min-w-[195px] w-full md:w-[276px] md:mt-0 mt-10 h-[fit-content] flex flex-col space-y-4 p-4 py-10 justify-center items-center rounded-[16px]' style={{boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)'}}>

          {
            !isMobile ? 
            KeywordAndLocation()
            : null
          }

          <div className='w-full flex flex-col justify-center space-y-3 md:mt-10'>

            <p>Filter by Category:</p>
            <CategoryFilters
              categoryFilters={categoryFilters}
              setCategoryFilters={setCategoryFilters}
            />

          </div>

          
        </div>

        <div className='flex flex-col items-center md:w-[1090px]'>

          <div className='flex w-full items-center'>

            <div className='flex flex-col'>
              <h1 id="search-results-top" className="font-bold font-[25px] mb-[10px]">Your AgeMD Search Results</h1>
              <p className="mb-[10px]">
                {totalResults > 0 ?
                (
                  totalResults > 1 ?
                  `Showing ${(page === 0 ? 1 : page * 18)} - ${totalResults > 18 ? ((page * 18) + 18) : totalResults} of ${totalResults} Results`
                  : `Showing 1 result`
                )
                : ``
                }
              </p>
            </div>

          </div>


          {searchResults.length > 0 ?

            (
            <div className='w-full flex flex-col justify-center items-center space-y-[30px]'>

              {searchResults.map((user: User, i) => (
                <SearchResultsContainer

                  key={i}
                  user={user}

                />
              ))}

            </div>
            ) 
            
            : 
            
            <p className='text-2xl'>No Results</p>}

          {

            totalPages && totalPages > 1 ?
            <div className='flex w-full justify-center py-20 md:p-20'>
              <Pagination count={totalPages ? totalPages : 0} page={page + 1} onChange={(event, page) => setPage(page - 1)} />
            </div>
            : null

          }

        </div>

        {

          isMobile ?

          KeywordAndLocation()

          :

          null

        }

      </div>
    </div>

  );

}

const App = () => {

  return (
    <AlgoliaProvider>
      <AppContent />
    </AlgoliaProvider>
  )

}

export default App;
